import React, { useEffect, useState } from "react";
import { Box, Grid, Chip, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import useFetchGroupById from "../../hooks/useFetchGroupById";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { IconBtnEditBase } from "../../common/utils/icons";
import Loader from "../../components/Molecules/Loader/Loader";
import { AssignRole, AssignTags } from "../../components/UserManagement";
import useFetchGroupRoles from "../../hooks/useFetchGroupRoles";
import useFetchGroupAuthTags from "../../hooks/useFetchGroupAuthTags";
import useFetchUsersByGroupId from "../../hooks/useFetchUsersByGroupId";
import { useAuth } from "../../contexts/AuthContext";

const ViewGroup: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { id: groupId } = useParams();

  // Permission checks for group actions
  const canEditGroup = hasPermission("user_group", "edit");
  const [formData, setFormData] = useState<any>({
    name: "",
    description: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch group data
  const [groupData] = useFetchGroupById({
    setIsLoading,
    groupId: groupId || null,
  });

  // Fetch group assignment data if editing
  const [groupAssignedRoles] = useFetchGroupRoles({
    setIsLoading,
    groupId: groupId || null,
  });

  const [groupAssignedAuthTags] = useFetchGroupAuthTags({
    setIsLoading,
    groupId: groupId || null,
  });
  // Fetch assigned users
  const [assignedUsers] = useFetchUsersByGroupId({
    setIsLoading,
    groupId: groupId || null,
  }) as [any[], () => Promise<void>];
  useEffect(() => {
    if (groupData) {
      setFormData({
        name: groupData?.name,
        description: groupData?.description,
      });
      setCurrentBreadcrumbPage({
        name: groupData?.name,
        id: groupData?.id,
      });
    }
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [groupData, setCurrentBreadcrumbPage]);

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <Box className="text-box-card-white">
          <Box className="text-box-header">
            <h3>Group Details</h3>
            {canEditGroup && (
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() => navigate(`/groups/edit/${groupId}`)}
              >
                <IconBtnEditBase
                  width={32}
                  height={32}
                  ariaLabel="Edit group"
                />
              </button>
            )}
          </Box>

          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Group Name</label>
              <TextField
                type="text"
                name="name"
                value={formData?.name}
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Description</label>
              <TextField
                type="text"
                name="description"
                value={formData?.description || "No description provided"}
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={4}
              xl={4}
              onClick={() => navigate(`/groups/${groupId}/assign-users`)}
            >
              <label className="label-text">Assigned User</label>
              <Box
                onClick={() => navigate(`/groups/${groupId}/assign-users`)}
                className="nav-link"
              >
                {assignedUsers?.length > 999
                  ? `${(assignedUsers.length / 1000).toFixed(1)}k`
                  : assignedUsers?.length || 0}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Assignment Sections */}
        <Grid container rowSpacing={1.5} columnSpacing={2.5}>
          {/* Assigned Roles Section */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <AssignRole
              selectedRolesIds={[]}
              onRoleChange={() => {}}
              viewMode={true}
              assignedRoles={groupAssignedRoles}
              label="Assigned Roles"
            />
          </Grid>

          {/* Assigned Tags Section */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <AssignTags
              selectedTags={[]}
              onTagChange={() => {}}
              viewMode={true}
              assignedTags={groupAssignedAuthTags}
              label="Assigned Tags"
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ViewGroup;
